import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { Form, Input, InputNumber } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import { Expense } from "../../../types/expenseTypes";
import { Supplier } from "../../../types/suppliersTypes";
import moment from "moment";
import CommonSelectLite from "components/Common/CommonSelectLite";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { parseSQLDate } from "utils/dateUtils";
import { getLabelPaymentMethodByType } from "utils/stringUtils";
import { PaymentMethodType } from "types/paymentMethodTypes";
import PaymentMethodExtraFields from "../components/PaymentMethodExtraFields";
import CustomFileUpload from "components/Common/ui/Files/CustomFileUpload";

interface AddEditExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  expenseData: Expense | null;
  onSave: (data: any) => void;
  suppliers: Supplier[];
  paymentMethods: PaymentMethodType[];
}

const AddEditExpenseModal: React.FC<AddEditExpenseModalProps> = ({
  isOpen,
  onClose,
  expenseData,
  onSave,
  suppliers,
  paymentMethods,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const maxSizeMB = process.env.REACT_APP_MAX_FILE_SIZE_MB
    ? Number(process.env.REACT_APP_MAX_FILE_SIZE_MB)
    : 5;

  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState<string | null>(
    null
  );
  const [isReceiptDeleted, setIsReceiptDeleted] = useState(false);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const formData = new FormData();

      // ✅ append non-file fields
      Object.entries(values).forEach(([key, value]) => {
        if (
          key !== "receiptFile" &&
          key !== "transferProof" &&
          key !== "deleteTransferProof"
        ) {
          if (key === "expenseDate" && moment.isMoment(value)) {
            formData.append(key, value.format("YYYY-MM-DD"));
          } else if (value !== undefined && value !== null) {
            formData.append(key, String(value));
          }
        }
      });

      if (values.receiptFile) {
        formData.append("receiptFile", values.receiptFile);
      }

      if (values.transferProof) {
        formData.append("transferProof", values.transferProof);
      }

      // delete receipt file if removed and no new file
      if (isReceiptDeleted && !values.receiptFile) {
        formData.append("deleteReceiptFile", "true");
      }

      if (values.deleteTransferProof && !values.transferProof) {
        formData.append("deleteTransferProof", "true");
      }

      await onSave(formData);
    } catch (error) {
      console.error("Error saving expense:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expenseData) {
      form.setFieldsValue({
        description: expenseData.description,
        supplierId: expenseData.supplierId,
        amount: expenseData.amount,
        expenseDate: expenseData.expenseDate
          ? parseSQLDate(expenseData.expenseDate)
          : null,
        receiptNumber: expenseData.receiptNumber,
        notes: expenseData.notes,
        status: expenseData.status,
        paymentMethodId: expenseData.paymentMethodId,
        bankName: expenseData.bankName,
        chequeReference: expenseData.chequeReference,
        deleteTransferProof: false, // reset flag on load
      });

      setExistingReceiptUrl(expenseData?.receiptFile || null);
      setIsReceiptDeleted(false);
      setReceiptFiles([]);
      form.setFieldsValue({ receiptFile: null });
    } else {
      form.resetFields();
      setReceiptFiles([]);
      setExistingReceiptUrl(null);
      setIsReceiptDeleted(false);
    }
  }, [expenseData, form]);

  const expenseStatus = [
    { value: "pending", label: t("expenses_status.pending") },
    { value: "approved", label: t("expenses_status.approved") },
    { value: "rejected", label: t("expenses_status.rejected") },
  ];

  const paymentMethodsOptions = useMemo(() => {
    return (paymentMethods || []).map((method) => ({
      value: method.id,
      label: getLabelPaymentMethodByType(method.type),
    }));
  }, [paymentMethods, t]);

  return (
    <CustomModal
      title={expenseData ? t("edit_expense") : t("add_expense")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={expenseData ? t("save") : t("add")}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("description")}
          name="description"
          rules={[
            { required: true, message: t("validation.please_enter_description") },
          ]}
        >
          <Input placeholder={t("description")} />
        </Form.Item>

        <Form.Item
          label={t("supplier")}
          name="supplierId"
          rules={[
            { required: true, message: t("validation.please_select_supplier") },
          ]}
        >
          <CommonSelectLite
            placeholder={t("select_supplier")}
            options={suppliers}
            value={form.getFieldValue("supplierId")}
            onChange={(val) => form.setFieldsValue({ supplierId: val })}
            optionValueKey="id"
            optionLabelKey="name"
          />
        </Form.Item>

        <Form.Item
          label={t("amount")}
          name="amount"
          rules={[{ required: true, message: t("validation.please_enter_amount") }]}
        >
          <InputNumber
            className="w-100"
            placeholder={t("amount")}
            min={0}
            precision={2}
            addonAfter="MAD"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
          />
        </Form.Item>

        <Form.Item
          label={t("expense_date")}
          name="expenseDate"
          rules={[{ required: true, message: t("validation.please_select_date") }]}
        >
          <LocalizedDatePicker className="w-100" placeholder={t("select_date")} />
        </Form.Item>

        <Form.Item
          label={t("receipt_number")}
          name="receiptNumber"
          rules={[
            {
              required: false,
              message: t("validation.please_enter_receipt_number"),
            },
          ]}
        >
          <Input placeholder={t("receipt_number")} />
        </Form.Item>
        <Form.Item
          label={t("paymentMethod.title")}
          name="paymentMethodId"
          rules={[
            {
              required: true,
              message: t("validation.please_select_payment_method"),
            },
          ]}
        >
          <CommonSelectLite
            placeholder={t("paymentMethod.title")}
            options={paymentMethodsOptions}
            value={form.getFieldValue("paymentMethodId")}
            onChange={(val) => form.setFieldsValue({ paymentMethodId: val })}
            optionValueKey="value"
            optionLabelKey="label"
          />
        </Form.Item>

        <PaymentMethodExtraFields
          form={form}
          paymentMethods={paymentMethods}
          paymentMethodIdField="paymentMethodId"
          i18nBase="paymentMethod"
          accept=".pdf,.jpg,.jpeg,.png"
          maxCount={1}
          maxSizeMB={maxSizeMB}
          hasExistingTransferProof={!!(expenseData as any)?.transferProof}
          existingTransferProofUrl={(expenseData as any)?.transferProof || null}
        />

        <Form.Item
          label={t("notes")}
          name="notes"
          rules={[{ required: true, message: t("validation.please_enter_notes") }]}
        >
          <Input.TextArea placeholder={t("notes")} />
        </Form.Item>

        <Form.Item name="receiptFile" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item label={t("receipt_file")}>
          <CustomFileUpload
            multiple={false}
            maxCount={1}
            maxSizeMB={maxSizeMB}
            accept=".pdf,.jpg,.jpeg,.png"
            value={receiptFiles}
            onChange={(files: File[]) => {
              setReceiptFiles(files);
              form.setFieldsValue({
                receiptFile: files[0] ?? null,
              });
              if (files.length > 0) {
                setIsReceiptDeleted(false);
              }
            }}
            existingFiles={
              !isReceiptDeleted && existingReceiptUrl
                ? [existingReceiptUrl]
                : []
            }
            onRemoveExisting={() => {
              setIsReceiptDeleted(true);
              setExistingReceiptUrl(null);
              form.setFieldsValue({ receiptFile: null });
              setReceiptFiles([]);
            }}
            showUploadList
            listType="text"
          />
        </Form.Item>

        <Form.Item
          label={t("status")}
          name="status"
          rules={[
            { required: true, message: t("validation.please_select_status") },
          ]}
        >
          <CommonSelectLite
            placeholder={t("select_status")}
            options={expenseStatus}
            value={form.getFieldValue("status")}
            onChange={(val) => form.setFieldsValue({ status: val })}
            optionValueKey="value"
            optionLabelKey="label"
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddEditExpenseModal;
